import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';

export default function TurnkeySolutionsPage() {
  const solutions = [
    { icon: 'ri-truck-line', title: 'Equipment Leasing', description: 'Flexible short-term and long-term leasing options for all imaging modalities with transparent pricing and no hidden fees.' },
    { icon: 'ri-map-pin-line', title: 'Delivery & Logistics', description: 'Complete delivery, positioning, and site coordination managed by our experienced logistics team.' },
    { icon: 'ri-flashlight-line', title: 'Power & Connectivity', description: 'Full electrical infrastructure assessment, power supply coordination, and network connectivity setup.' },
    { icon: 'ri-tools-line', title: 'Installation & Setup', description: 'Professional equipment positioning, calibration, and operational testing prior to handoff to your team.' },
    { icon: 'ri-calendar-check-line', title: 'Preventative Maintenance', description: 'Ongoing maintenance programs to ensure maximum equipment uptime and optimal performance throughout the lease.' },
    { icon: 'ri-shield-check-line', title: 'Equipment Compliance', description: 'Equipment licensing, radiation safety documentation, and quality assurance protocols for the hardware we provide.' },
    { icon: 'ri-customer-service-line', title: '24/7 Technical Support', description: 'Round-the-clock technical support and emergency response for all deployed equipment — hardware issues only.' },
    { icon: 'ri-refresh-line', title: 'Equipment Swap & Upgrade', description: 'Mid-lease equipment upgrades or emergency swap-outs to keep your facility running without interruption.' }
  ];

  const process = [
    { step: '01', title: 'Lease Equipment', description: 'Select your imaging modality and lease term. We structure a contract around your timeline and budget.' },
    { step: '02', title: 'We Deliver & Install', description: 'Our logistics team delivers, positions, and connects the equipment. We handle power, placement, and site readiness.' },
    { step: '03', title: 'Your Team Operates', description: 'Your licensed clinical staff takes over. All imaging services, patient care, and billing remain entirely under your control.' },
    { step: '04', title: 'We Maintain Equipment', description: 'We provide ongoing preventative maintenance and uptime monitoring throughout the lease term.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main className="pt-24 sm:pt-32 pb-12 sm:pb-20">
        <section className="px-4 sm:px-6 mb-10 sm:mb-16">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-slate-blue text-[13px] uppercase tracking-[2px] font-medium mb-3 sm:mb-4">DEPLOYMENT & INFRASTRUCTURE</p>
            <h1 className="text-navy text-[28px] sm:text-[40px] lg:text-[48px] font-semibold leading-tight mb-4 sm:mb-6">Equipment Leasing &amp; Deployment Programs</h1>
            <p className="text-[#5A6C7D] text-base sm:text-lg leading-relaxed">We provide the equipment, deliver it, install it, and maintain it. Your clinical team operates it. That&apos;s the full scope of our role.</p>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="px-4 sm:px-6 mb-12 sm:mb-16">
          <div className="max-w-4xl mx-auto bg-navy/5 border-l-4 border-navy rounded-r-xl p-6 sm:p-8">
            <h2 className="text-navy text-base font-semibold mb-2 uppercase tracking-wide">Operational Responsibility</h2>
            <p className="text-[#5A6C7D] text-sm sm:text-[15px] leading-relaxed">The Company provides medical imaging equipment only. All clinical services — including staffing, scheduling, patient care, billing, and compliance — are the sole responsibility of the healthcare provider. The Company does not provide radiology services, technologists, or clinical personnel.</p>
          </div>
        </section>

        {/* How It Works */}
        <section className="px-4 sm:px-6 py-12 sm:py-20 bg-[#F7F9FB]">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-navy text-[24px] sm:text-[30px] lg:text-[36px] font-semibold text-center mb-10 sm:mb-16">How It Works</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
              {process.map((item, index) => (
                <div key={index} className="relative">
                  <div className="bg-white rounded-xl p-6 sm:p-8 h-full">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-navy rounded-full flex items-center justify-center mb-4 sm:mb-6">
                      <span className="text-white text-xl sm:text-2xl font-bold">{item.step}</span>
                    </div>
                    <h3 className="text-navy text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{item.title}</h3>
                    <p className="text-[#5A6C7D] text-sm sm:text-[15px] leading-relaxed">{item.description}</p>
                    {item.step === '03' && (
                      <span className="inline-block mt-3 bg-slate-blue/10 text-slate-blue text-xs font-semibold px-3 py-1 rounded-full">Client runs the operation</span>
                    )}
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <i className="ri-arrow-right-line text-slate-blue text-3xl"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Solutions Grid */}
        <section className="px-4 sm:px-6 py-12 sm:py-20">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-navy text-[24px] sm:text-[30px] lg:text-[36px] font-semibold text-center mb-10 sm:mb-16">What&apos;s Included in Every Lease</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8">
              {solutions.map((solution, index) => (
                <div key={index} className="bg-white rounded-xl p-6 sm:p-8 border border-gray-100 hover:border-slate-blue/20 transition-all duration-300">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-blue rounded-full flex items-center justify-center mb-4 sm:mb-6">
                    <i className={`${solution.icon} text-white text-xl sm:text-2xl`}></i>
                  </div>
                  <h3 className="text-navy text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{solution.title}</h3>
                  <p className="text-[#5A6C7D] text-sm sm:text-[15px] leading-relaxed">{solution.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 sm:px-6 mt-4 sm:mt-8">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-navy to-navy-light rounded-2xl sm:rounded-3xl p-8 sm:p-12 lg:p-16 text-center">
            <h2 className="text-white text-[24px] sm:text-[30px] lg:text-[36px] font-semibold mb-3 sm:mb-4">Ready to Lease Equipment?</h2>
            <p className="text-white/85 text-base sm:text-lg mb-6 sm:mb-8">Let our leasing team design a deployment program for your facility.</p>
            <a href="/contact" className="inline-block bg-white text-navy px-8 sm:px-10 py-3.5 sm:py-4 rounded-full text-base font-semibold hover:shadow-2xl transition-all duration-300 cursor-pointer whitespace-nowrap">Request a Lease Quote</a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
